import { AfterViewInit, Component, computed, inject, input } from "@angular/core";

import { ThreeDSTokenResponse } from "@modules/checkout/types/3ds-token-response";
import { ThreeDSService } from "@modules/checkout/services/3ds/3ds.service";
import { Product } from "@modules/checkout/types/product";
import { parseValidThru } from "@shared/utils/date.utils";

// All bpmpi fields this component renders statically.
// Context["3DS"] entries matching these keys are ignored to prevent
// duplicate class names in the DOM, which would produce undefined
// behavior in the bpmpi script (getElementsByClassName returns a
// collection and the script's handling of duplicates is unknown).
const HANDLED_FIELDS = new Set([
  "bpmpi_auth",
  "bpmpi_auth_notifyonly",
  "bpmpi_auth_suppresschallenge",
  "bpmpi_accesstoken",
  "bpmpi_ordernumber",
  "bpmpi_totalamount",
  "bpmpi_currency",
  "bpmpi_installments",
  "bpmpi_paymentmethod",
  "bpmpi_order_recurrence",
  "bpmpi_order_productcode",
  "bpmpi_merchant_url",
  "bpmpi_cardnumber",
  "bpmpi_cardexpirationmonth",
  "bpmpi_cardexpirationyear",
  "bpmpi_useraccount_guest",
  "bpmpi_device_channel",
  "bpmpi_device_ipaddress",
  "bpmpi_brand_establishment_code",
]);

@Component({
  selector: "app-bpmpi-form",
  templateUrl: "./bpmpi-form.component.html",
})
export class BpmpiFormComponent implements AfterViewInit {
  private readonly threeDSService = inject(ThreeDSService);

  readonly tokenResponse = input<ThreeDSTokenResponse | null>(null);
  readonly orderId = input<string>("");
  readonly cardNumber = input<string>("");
  readonly validThru = input<string>("");
  readonly installments = input<number>(1);
  readonly totalAmount = input<number>(0);
  readonly products = input<Product[]>([]);

  ngAfterViewInit(): void {
    this.threeDSService.formReady();
  }

  protected readonly isEnabled = computed(() => this.tokenResponse()?.Enabled === true);

  protected readonly accessToken = computed(() => {
    const r = this.tokenResponse();
    return r?.Enabled ? (r.AccessToken ?? "") : "";
  });

  protected readonly establishmentCode = computed(() => {
    const r = this.tokenResponse();
    return r?.Enabled ? String(r.EstablishmentCode ?? "") : "";
  });

  protected readonly deviceIpAddress = computed(() => {
    const r = this.tokenResponse();
    return r?.Enabled ? (r.DeviceIpAddress ?? "") : "";
  });

  protected readonly rawCardNumber = computed(() => this.cardNumber().replace(/\D/g, ""));

  private readonly parsedValidThru = computed(() => parseValidThru(this.validThru()));
  protected readonly expMonth = computed(() => this.parsedValidThru().month);
  protected readonly expYear = computed(() => this.parsedValidThru().year);

  protected readonly merchantUrl = computed(
    () => `${window.location.protocol}//${window.location.host}`,
  );

  protected readonly contextFields = computed(() => {
    const r = this.tokenResponse();
    if (!r?.Enabled || !r.Context) return [];
    return Object.entries(r.Context)
      .filter(([key]) => !HANDLED_FIELDS.has(key))
      .map(([key, value]) => ({ key, value: String(value ?? "") }));
  });
}
